import { ReactNode, useEffect, useRef } from "react";
import { Navigate } from "react-router-dom";
import { useAuthStore } from "../../stores/authStore";

/**
 * Quien ya tiene sesión no se queda en la portada: se va a la app.
 *
 * El sitio público es la vitrina para quien todavía no entra. A quien ya entró, la
 * portada no le ofrece nada que la app no tenga —está `/app/inicio` con el mismo
 * contenido— y lo deja a un clic de distancia de donde iba.
 *
 * **Hay que preguntar por la sesión.** El store solo consulta `/me/` dentro de `/app`
 * (lo hace `RequireAuth`), así que acá `user` empieza en `null` aunque haya sesión: no
 * es que no la haya, es que nadie preguntó. Por eso se consulta al montar, salvo que el
 * store ya tenga la respuesta de antes —al navegar desde la app, por ejemplo—, que es
 * cuando el salto sale instantáneo y sin pedir nada.
 *
 * Mientras se pregunta **se muestra la portada**, no una pantalla de carga. Casi todo
 * el que llega acá es un visitante anónimo, y hacerlo esperar a una consulta que le va
 * a decir que no tiene sesión sería cobrarle a la mayoría el atajo de unos pocos. El
 * costo es que quien sí tiene sesión alcanza a ver la portada un instante antes de
 * saltar; es un ida y vuelta al servidor, y solo entrando en frío.
 */
export default function RedirigirSiHaySesion({ children }: { children: ReactNode }) {
  const { user, checkAuth } = useAuthStore();
  // Ref y no estado: preguntar una vez por montaje, sin provocar otro render.
  const preguntado = useRef(false);

  useEffect(() => {
    if (!user && !preguntado.current) {
      preguntado.current = true;
      checkAuth();
    }
  }, [user, checkAuth]);

  if (user) return <Navigate to="/app" replace />;
  return <>{children}</>;
}
